import { formatCopy } from "../theme/copy";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ComponentType, ReactNode } from "react";
import { Box, Button, Typography } from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import { useThemeContext } from "../globalContext/global-context";

export type CompletionLayout = ComponentType<{
  message: ReactNode;
  actions: ReactNode;
}>;

type CompletionControlProps = {
  content: string;
  redirect?: string;
  numberSeconds?: number;
  startedAtMs?: number;
  buttonLabel?: string;
  layout: CompletionLayout;
  active: boolean;
  canUndo: boolean;
  onUndo: () => void;
  onRedirect: (url: string) => void;
};

export const CompletionControl = ({
  content,
  redirect,
  numberSeconds,
  startedAtMs,
  buttonLabel,
  layout: Layout,
  active,
  canUndo,
  onUndo,
  onRedirect,
}: CompletionControlProps) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const copy = theme.copy;
  const duration = numberSeconds ?? 3;
  const mountedAtMs = useRef(Date.now());
  const deadline =
    (startedAtMs ?? mountedAtMs.current) + Math.max(0, duration) * 1000;
  const [remainingSeconds, setRemainingSeconds] = useState(() =>
    Math.max(0, Math.ceil((deadline - Date.now()) / 1000)),
  );
  const [cancelled, setCancelled] = useState(false);
  const finished = useRef(false);
  const redirectOnce = useCallback(() => {
    if (!redirect || finished.current) return;
    finished.current = true;
    setCancelled(true);
    onRedirect(redirect);
  }, [redirect, onRedirect]);

  useEffect(() => {
    if (!redirect || !active || cancelled) return;
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setRemainingSeconds(remaining);
      if (remaining === 0) {
        clearInterval(timer);
        redirectOnce();
      }
    };
    const timer = setInterval(tick, 250);
    tick();
    return () => clearInterval(timer);
  }, [redirect, active, cancelled, deadline, redirectOnce]);

  const handleUndo = () => {
    finished.current = true;
    setCancelled(true);
    onUndo();
  };

  const messageElement = (
    <>
      <Typography id="completion-title">{content}</Typography>
      <Typography>
        {redirect
          ? formatCopy(copy.redirect, { seconds: remainingSeconds })
          : copy.close}
      </Typography>
    </>
  );
  const actionsElement = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        gap: `${theme.tokens.spacing["2"]}px`,
      }}
    >
      {canUndo && (
        <Button
          variant="outlined"
          onClick={handleUndo}
          startIcon={<HistoryIcon />}
        >
          {copy.actions.undo}
        </Button>
      )}
      <Button variant="contained" onClick={redirectOnce}>
        {buttonLabel ?? copy.actions.confirm}
      </Button>
    </Box>
  );

  return <Layout message={messageElement} actions={actionsElement} />;
};
